import React, { createContext, PropsWithChildren, useContext, useEffect, useRef } from 'react';

import { useFirebaseMessaging } from '@/hooks/use-firebase-messaging';
import useRollbar from '@/hooks/use-rollbar';
import { User } from '@/typings/user';
import { initMessaging, updateData } from '@/utils/firebase';

import { useAuth } from './auth-provider/auth-provider';

type NotificationBannerType = {
  requestPermission: () => Promise<void>;
  token: string | null;
  permissionStatus: NotificationPermission;
};

const NotificationBannerContext = createContext({} as NotificationBannerType);

const NotificationBannerProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { user } = useAuth();
  const verifiedToken = useRef(false);
  const [requestPermission, { permissionStatus, token }] = useFirebaseMessaging();
  const rollbar = useRollbar();

  useEffect(() => {
    if (!user) return; // Should only post to the backend when the user is available
    if (verifiedToken.current) return; // Token already posted

    const userMissingTokens = (token?: string | null) => {
      const tokens = (user.notificationTokensWeb || []).filter((tk) => !tk.deactivatedAt);
      if (!tokens) return true;
      if (token && !tokens.some((tk) => tk.token === token)) return true;
      return false;
    };
    if (!userMissingTokens(token)) return; // User already have tokens

    const handleTokenChange = (token: string) => {
      const existingTokens = user.notificationTokensWeb || [];
      updateData<User>(['users'], user.id, {
        notificationTokensWeb: [
          ...existingTokens,
          {
            token,
            userAgent: navigator.userAgent,
            successCount: 0,
            failCount: 0,
            createdAt: new Date().toISOString(),
          },
        ],
      }).catch((err) => {
        rollbar.error('AuthProvider - Falha ao atualizar token de notificação', err as Error);
      });
    };

    if (token) {
      verifiedToken.current = true;
      handleTokenChange(token);
      return;
    }

    // No token
    if (permissionStatus === 'granted') {
      // This code sets the notification token in the backend if the user has already allowed notifications before and does not have any notificationToken.
      // A situation that it might happen:
      // User logs in with an account and then accept notification permission.
      // After a while he logs out and then reconnect with a different account and the permission was already granted before
      verifiedToken.current = true;

      initMessaging(true)
        .then((newToken) => newToken && handleTokenChange(newToken))
        .catch((err) =>
          rollbar.error('Failed to start Firebase cloud messaging - notificationBanner', err),
        );
    }
  }, [user, permissionStatus, token]);

  return (
    <NotificationBannerContext.Provider value={{ permissionStatus, token, requestPermission }}>
      {children}
    </NotificationBannerContext.Provider>
  );
};

export const useNotificationBanner = () => useContext(NotificationBannerContext);

export default NotificationBannerProvider;
